import { InputNumberLocalized } from '@components'

import { Divider, Flex, Space, Spacing, Text } from '@billy/design-system'
import { FocusEvent, KeyboardEvent, useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { NumberValue } from '../../../../../../../../../../../../../../utils'
import { MAX_PAYMENT_TERM_DAYS } from './constants/maxPaymentTermDays'
import * as Styled from './styles'

interface PaymentTermSelectFooterProps {
  currentPaymentTerm?: number
  onPaymentTermChange: (value: number) => void
}

export const PaymentTermSelectFooter = ({ currentPaymentTerm, onPaymentTermChange }: PaymentTermSelectFooterProps) => {
  const [isFocused, setIsFocused] = useState(!!currentPaymentTerm)
  const { t } = useTranslation()

  const applyCustomPaymentTerm = useCallback(
    (input: HTMLInputElement) => {
      if (!input.value) {
        return
      }

      const inputNumberValue = new NumberValue(input.value).getValue()

      if (inputNumberValue <= MAX_PAYMENT_TERM_DAYS) {
        onPaymentTermChange(inputNumberValue)
      }
    },
    [onPaymentTermChange],
  )

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      applyCustomPaymentTerm(event.target)
    },
    [applyCustomPaymentTerm],
  )

  const handlePressEnter = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      applyCustomPaymentTerm(event.currentTarget)
    },
    [applyCustomPaymentTerm],
  )

  const handleOnFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  return (
    <>
      <Space size="s4" />
      <Divider />
      <Space size="s4" />
      <Styled.PaymentTermSelectWrapper isInputFocused={isFocused}>
        <Flex alignItems="center" gap={Spacing.S8}>
          <Trans
            i18nKey="external_invoices.editor.form.field.due_date_and_payment_term.select_term.custom_days_count"
            count={currentPaymentTerm}
          >
            <InputNumberLocalized
              isFallbackToZero={false}
              keyboard
              min={0}
              onBlur={handleBlur}
              onFocus={handleOnFocus}
              onPressEnter={handlePressEnter}
              placeholder={t(
                'external_invoices.editor.form.field.due_date_and_payment_term.custom_payment_term.placeholder',
              )}
              value={currentPaymentTerm}
              withCalculations={false}
              withFormatting={false}
            />
            <Text as="span">day</Text>
          </Trans>
        </Flex>
      </Styled.PaymentTermSelectWrapper>
    </>
  )
}
